// @flow

import React, { Component } from 'react';
import { withRouter } from 'react-router';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Row,
    Spinner,
    Table,
} from 'reactstrap';
import type { RouterHistory } from 'react-router';
import {
    fetchOverviewMetrics,
    formatTimestamp,
    getStatusTone,
    formatMetricValue,
    formatMoney,
    getRiskScope,
    getRiskScopeLabel,
    getRiskScopeTone,
} from './dashboardUtils';
import PaymentLedgerExport from './PaymentLedgerExport';

type PropsType = {
    history: RouterHistory,
};

type StateType = {
    loading: boolean,
    error: ?string,
    metrics: any,
    activeDrilldownKey: ?string,
};

class OperationsOverviewDashboard extends Component<PropsType, StateType> {
    constructor(props: PropsType) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            metrics: null,
            activeDrilldownKey: null,
        };
    }

    componentDidMount() {
        this.load();
    }

    load = () => {
        this.setState({ loading: true, error: null });
        fetchOverviewMetrics()
            .then((metrics) => {
                this.setState({
                    loading: false,
                    metrics,
                    activeDrilldownKey: null,
                });
            })
            .catch((error) => {
                this.setState({
                    loading: false,
                    error: error.toString(),
                });
            });
    };

    setActiveDrilldown = (drilldownKey: string) => {
        this.setState({ activeDrilldownKey: drilldownKey });
    };

    clearDrilldown = () => {
        this.setState({ activeDrilldownKey: null });
    };

    onSummaryCardKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>, drilldownKey: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.setActiveDrilldown(drilldownKey);
        }
    };

    getActiveCard() {
        const { metrics, activeDrilldownKey } = this.state;

        if (!metrics || !activeDrilldownKey) {
            return null;
        }

        return metrics.summaryCards.find((card) => card.drilldownKey === activeDrilldownKey) || null;
    }

    renderHero() {
        const { history } = this.props;

        return (
            <div className="operations-hero">
                <div>
                    <div className="operations-kicker">BillMaster live operations</div>
                    <h1 className="operations-title">Operations overview</h1>
                    <p className="operations-subtitle">
                        Monitor restaurants, payments, loyalty activity, and support workload from one place.
                    </p>
                </div>
                <div className="operations-hero-actions">
                    <Button color="primary" onClick={() => history.push('/support-monitor')}>
                        Open support hub
                    </Button>
                    <Button color="secondary" outline onClick={() => history.push('/restaurant-activity')}>
                        View restaurant activity
                    </Button>
                    <Button color="secondary" outline onClick={() => history.push('/bill-list')}>
                        Inspect live bills
                    </Button>
                </div>
            </div>
        );
    }

    renderSummaryCards() {
        const { metrics, activeDrilldownKey } = this.state;
        return (
            <Row>
                {metrics.summaryCards.map((card) => {
                    const riskScope = getRiskScope(card.drilldownKey);
                    return (
                        <Col lg="4" md="6" key={card.label} className="bottom-spaced-element">
                            <Card
                                className={`dashboard-card dashboard-card-action tone-${card.tone || 'neutral'} ${activeDrilldownKey === card.drilldownKey ? 'is-active' : ''}`}
                                data-testid={`operations-summary-card-${card.drilldownKey}`}
                                onClick={() => this.setActiveDrilldown(card.drilldownKey)}
                                onKeyDown={(event) => this.onSummaryCardKeyDown(event, card.drilldownKey)}
                                role="button"
                                tabIndex="0"
                            >
                                <CardBody>
                                    {riskScope ? (
                                        <Badge color={getRiskScopeTone(riskScope)} pill className="mb-2">
                                            {getRiskScopeLabel(riskScope)}
                                        </Badge>
                                    ) : null}
                                    <div className="dashboard-card-label">{card.label}</div>
                                    <div className="dashboard-card-value">{formatMetricValue(card)}</div>
                                    <div className="dashboard-card-help">{card.help}</div>
                                    <div className="dashboard-card-cta">View details</div>
                                </CardBody>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        );
    }

    renderPilotAlertSummary() {
        const { metrics } = this.state;
        const summary = metrics.pilotAlertSummary;
        if (!summary) {
            return null;
        }

        return (
            <Alert color={getStatusTone(summary.severity)} data-testid="operations-alert-summary" className="bottom-spaced-element">
                <div className="dashboard-list-title">{summary.title}</div>
                <div>{summary.signal}</div>
                <div className="dashboard-inline-meta">Evidence: {summary.evidence}</div>
                <div className="dashboard-inline-meta">First action: {summary.firstAction}</div>
            </Alert>
        );
    }

    renderOverviewDrilldownContent() {
        const { history } = this.props;
        const { metrics, activeDrilldownKey } = this.state;

        switch (activeDrilldownKey) {
            case 'enabledRestaurants':
                return metrics.drilldowns.enabledRestaurants.length === 0 ? (
                    <div className="dashboard-empty-state">No enabled restaurants are available in the latest sample.</div>
                ) : (
                    <Table responsive className="dashboard-table" data-testid="overview-drilldown-table-enabledRestaurants">
                        <thead>
                            <tr>
                                <th>Restaurant</th>
                                <th>Enabled locations</th>
                                <th>Total locations</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.drilldowns.enabledRestaurants.map((organization) => (
                                <tr key={organization.id}>
                                    <td>{organization.name}</td>
                                    <td>{organization.enabledLocations}</td>
                                    <td>{organization.totalLocations}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                );
            case 'activeGuestSessions':
                return metrics.drilldowns.activeGuestSessions.length === 0 ? (
                    <div className="dashboard-empty-state">No active guest sessions were found in the latest sample.</div>
                ) : (
                    <Table responsive hover className="dashboard-table" data-testid="overview-drilldown-table-activeGuestSessions">
                        <thead>
                            <tr>
                                <th>Location</th>
                                <th>Organization</th>
                                <th>Table</th>
                                <th>Customer</th>
                                <th>Payment mode</th>
                                <th>Opened</th>
                                <th>Expires</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.drilldowns.activeGuestSessions.map((session) => (
                                <tr
                                    key={session.id}
                                    onClick={() => history.push(`/org_location-info/${session.locationId}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>{session.locationName}</td>
                                    <td>{session.organizationName}</td>
                                    <td>{session.tableNumber}</td>
                                    <td>{session.customerNumber}</td>
                                    <td>{session.paymentMode || 'Unknown'}</td>
                                    <td>{formatTimestamp(session.createdAt)}</td>
                                    <td>{formatTimestamp(session.expiresAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                );
            case 'paymentsNeedingAttention':
                return metrics.drilldowns.paymentsNeedingAttention.length === 0 ? (
                    <div className="dashboard-empty-state">No payment issues were found in the latest sample.</div>
                ) : (
                    <Table responsive hover className="dashboard-table" data-testid="overview-drilldown-table-paymentsNeedingAttention">
                        <thead>
                            <tr>
                                <th>Transaction</th>
                                <th>Location</th>
                                <th>Bill</th>
                                <th>Status</th>
                                <th>Provider</th>
                                <th>Provider Tx</th>
                                <th>Amount</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.drilldowns.paymentsNeedingAttention.map((alert) => (
                                <tr
                                    key={alert.id}
                                    onClick={() => history.push(`/bill-info/${alert.billId}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>{alert.id}</td>
                                    <td>
                                        <div>{alert.locationName}</div>
                                        <div className="dashboard-inline-meta">{alert.organizationName}</div>
                                    </td>
                                    <td>{alert.billId}</td>
                                    <td><Badge color="danger">{alert.status}</Badge></td>
                                    <td>{alert.provider || 'payfacto'}</td>
                                    <td style={{ maxWidth: 160, wordBreak: 'break-all' }}>
                                        {alert.providerTxId || alert.merchantNo || '—'}
                                    </td>
                                    <td>{formatMoney(alert.amount)}</td>
                                    <td>{formatTimestamp(alert.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                );
            case 'openSupportWork':
                return (
                    <div className="dashboard-empty-state">Open support work is still on the legacy internal workflow, so there are no queue rows to surface in this slice yet.</div>
                );
            case 'guestDistressSignals':
                return metrics.drilldowns.guestDistressSignals.length === 0 ? (
                    <div className="dashboard-empty-state">No guest distress signals were detected in the latest App Clip sample.</div>
                ) : (
                    <Table responsive hover className="dashboard-table" data-testid="overview-drilldown-table-guestDistressSignals">
                        <thead>
                            <tr>
                                <th>Signal</th>
                                <th>Location</th>
                                <th>Detail</th>
                                <th>Severity</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.drilldowns.guestDistressSignals.map((event) => (
                                <tr
                                    key={`${event.eventName}-${event.createdAt}-${event.deviceId || 'unknown'}`}
                                    onClick={() => {
                                        if (event.billId) {
                                            history.push(`/bill-info/${event.billId}`);
                                        } else if (event.locationId) {
                                            history.push(`/org_location-info/${event.locationId}`);
                                        }
                                    }}
                                    style={{ cursor: event.billId || event.locationId ? 'pointer' : 'default' }}
                                >
                                    <td>{event.title}</td>
                                    <td>
                                        <div>{event.locationName}</div>
                                        <div className="dashboard-inline-meta">{event.organizationName}</div>
                                    </td>
                                    <td>{event.detail}</td>
                                    <td><Badge color={getStatusTone(event.severity)}>{event.severity}</Badge></td>
                                    <td>{formatTimestamp(event.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                );
            case 'recentCompletedBillVolume':
                return metrics.drilldowns.recentCompletedBillVolume.length === 0 ? (
                    <div className="dashboard-empty-state">No completed bills were found in the latest sample.</div>
                ) : (
                    <Table responsive hover className="dashboard-table" data-testid="overview-drilldown-table-recentCompletedBillVolume">
                        <thead>
                            <tr>
                                <th>Bill</th>
                                <th>Location</th>
                                <th>Status</th>
                                <th>Total</th>
                                <th>Completed</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.drilldowns.recentCompletedBillVolume.map((bill) => (
                                <tr
                                    key={bill.id}
                                    onClick={() => history.push(`/bill-info/${bill.id}`)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <td>{bill.id}</td>
                                    <td>
                                        <div>{bill.locationName}</div>
                                        <div className="dashboard-inline-meta">{bill.organizationName}</div>
                                    </td>
                                    <td>{bill.status}</td>
                                    <td>{formatMoney(bill.total)}</td>
                                    <td>{formatTimestamp(bill.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                );
            case 'recentLoyaltyRedemptions':
                return metrics.drilldowns.recentLoyaltyRedemptions.length === 0 ? (
                    <div className="dashboard-empty-state">No loyalty redemptions were found in the latest sample.</div>
                ) : (
                    <Table responsive hover className="dashboard-table" data-testid="overview-drilldown-table-recentLoyaltyRedemptions">
                        <thead>
                            <tr>
                                <th>Redemption</th>
                                <th>Locations</th>
                                <th>Linked bills</th>
                                <th>Amount</th>
                                <th>Created</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metrics.drilldowns.recentLoyaltyRedemptions.map((event) => (
                                <tr
                                    key={event.id}
                                    onClick={() => {
                                        if (event.billIds.length > 0) {
                                            history.push(`/bill-info/${event.billIds[0]}`);
                                        }
                                    }}
                                    style={{ cursor: event.billIds.length > 0 ? 'pointer' : 'default' }}
                                >
                                    <td>{event.id}</td>
                                    <td>{event.locationNames.length > 0 ? event.locationNames.join(', ') : 'Unlinked'}</td>
                                    <td>{event.billIds.length > 0 ? event.billIds.join(', ') : 'None'}</td>
                                    <td>{formatMoney(event.amount)}</td>
                                    <td>{formatTimestamp(event.createdAt)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                );
            default:
                return null;
        }
    }

    renderDrilldownPanel() {
        const activeCard = this.getActiveCard();

        return (
            <Card className="dashboard-panel bottom-spaced-element" data-testid="overview-drilldown-panel">
                <CardHeader className="dashboard-panel-header dashboard-panel-header-with-action">
                    <div>
                        <div>{activeCard ? `${activeCard.label} details` : 'Metric drill-down'}</div>
                        <div className="dashboard-panel-subtitle">
                            {activeCard ? activeCard.help : 'Select a summary card to reveal the underlying records.'}
                        </div>
                    </div>
                    {activeCard ? (
                        <Button color="secondary" outline size="sm" onClick={this.clearDrilldown}>
                            Clear
                        </Button>
                    ) : null}
                </CardHeader>
                <CardBody>
                    {activeCard ? this.renderOverviewDrilldownContent() : (
                        <div className="dashboard-empty-state">Choose a metric above to inspect the detailed rows behind that summary.</div>
                    )}
                </CardBody>
            </Card>
        );
    }

    renderPaymentAlerts() {
        const { history } = this.props;
        const { metrics } = this.state;
        return (
            <Card className="dashboard-panel" data-testid="payment-alerts-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Payment alerts</span>
                    <Badge color={getRiskScopeTone('operator')} pill>Operator risk</Badge>
                </CardHeader>
                <CardBody>
                    {metrics.paymentAlerts.length === 0 ? (
                        <div className="dashboard-empty-state">
                            {metrics.resolvedPaymentHistory.length > 0
                                ? `No active payment issues detected. ${metrics.resolvedPaymentHistory.length} recent exceptions were already recovered or identified as demo history.`
                                : 'No recent payment issues detected.'}
                        </div>
                    ) : (
                        <Table responsive hover className="dashboard-table" data-testid="payment-alerts-table">
                            <thead>
                                <tr>
                                    <th>Transaction</th>
                                    <th>Bill</th>
                                    <th>Bill status</th>
                                    <th>Status</th>
                                    <th>Provider</th>
                                    <th>Provider Tx</th>
                                    <th>Amount</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metrics.paymentAlerts.map((alert) => (
                                    <tr
                                        key={alert.id}
                                        onClick={() => history.push(`/bill-info/${alert.billId}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{alert.id}</td>
                                        <td>{alert.billId}</td>
                                        <td>{alert.billStatus}</td>
                                        <td>
                                            <Badge color="danger">{alert.status}</Badge>
                                        </td>
                                        <td>{alert.provider || 'payfacto'}</td>
                                        <td style={{ maxWidth: 160, wordBreak: 'break-all' }}>
                                            {alert.providerTxId || alert.merchantNo || '—'}
                                        </td>
                                        <td>{formatMoney(alert.amount)}</td>
                                        <td>{formatTimestamp(alert.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        );
    }

    renderResolvedPaymentHistory() {
        const { history } = this.props;
        const { metrics } = this.state;

        return (
            <Card className="dashboard-panel" data-testid="resolved-payment-history-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Resolved or demo payment history</span>
                    <Badge color="secondary" pill>Context only</Badge>
                </CardHeader>
                <CardBody>
                    {metrics.resolvedPaymentHistory.length === 0 ? (
                        <div className="dashboard-empty-state">No resolved or demo payment exceptions were found in the latest sample.</div>
                    ) : (
                        <Table responsive hover className="dashboard-table" data-testid="resolved-payment-history-table">
                            <thead>
                                <tr>
                                    <th>Transaction</th>
                                    <th>Bill</th>
                                    <th>State</th>
                                    <th>Provider</th>
                                    <th>Provider Tx</th>
                                    <th>Status</th>
                                    <th>Amount</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metrics.resolvedPaymentHistory.map((alert) => (
                                    <tr
                                        key={alert.id}
                                        onClick={() => history.push(`/bill-info/${alert.billId}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{alert.id}</td>
                                        <td>
                                            <div>{alert.billId}</div>
                                            <div className="dashboard-inline-meta">{alert.billStatus}</div>
                                        </td>
                                        <td>
                                            <Badge color={alert.alertTone || 'secondary'}>{alert.alertStateLabel}</Badge>
                                            <div className="dashboard-inline-meta">{alert.resolutionDetail}</div>
                                        </td>
                                        <td>{alert.provider || 'payfacto'}</td>
                                        <td style={{ maxWidth: 160, wordBreak: 'break-all' }}>
                                            {alert.providerTxId || alert.merchantNo || '—'}
                                        </td>
                                        <td>{alert.status}</td>
                                        <td>{formatMoney(alert.amount)}</td>
                                        <td>{formatTimestamp(alert.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        );
    }

    renderSupportQueue() {
        const { metrics } = this.state;
        return (
            <Card className="dashboard-panel" data-testid="support-queue-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Support queue snapshot</span>
                    <Badge color={getRiskScopeTone('operator')} pill>Operator risk</Badge>
                </CardHeader>
                <CardBody>
                    {metrics.supportQueue.length === 0 ? (
                        <div className="dashboard-empty-state">No active support items in the latest sample.</div>
                    ) : (
                        <div className="dashboard-list" data-testid="support-queue-list">
                            {metrics.supportQueue.map((item) => (
                                <div className="dashboard-list-row" key={item.id}>
                                    <div>
                                        <div className="dashboard-list-title">{item.name}</div>
                                        <div className="dashboard-list-meta">
                                            {item.assignee} · Created {formatTimestamp(item.createdAt)}
                                        </div>
                                    </div>
                                    <div className="dashboard-list-right">
                                        <Badge color={getStatusTone(item.status === 'new' ? 'warning' : 'neutral')}>
                                            {item.status}
                                        </Badge>
                                        <div className="dashboard-list-meta">Follow-up {formatTimestamp(item.nextFollowUpAt)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>
        );
    }

    renderGuestWatchlist() {
        const { history } = this.props;
        const { metrics } = this.state;
        return (
            <Card className="dashboard-panel" data-testid="guest-watchlist-panel">
                <CardHeader className="dashboard-panel-header d-flex justify-content-between align-items-center flex-wrap">
                    <span>Guest risk watchlist</span>
                    <Badge color={getRiskScopeTone('guest')} pill>Guest risk</Badge>
                </CardHeader>
                <CardBody>
                    {metrics.guestWatchlist.length === 0 ? (
                        <div className="dashboard-empty-state">No guest-risk signals need action in the latest App Clip sample.</div>
                    ) : (
                        <div className="dashboard-list" data-testid="guest-watchlist-list">
                            {metrics.guestWatchlist.map((event) => (
                                <div
                                    className="dashboard-list-row"
                                    key={`${event.eventName}-${event.createdAt}-${event.deviceId || 'unknown'}`}
                                    onClick={() => {
                                        if (event.billId) {
                                            history.push(`/bill-info/${event.billId}`);
                                        } else if (event.locationId) {
                                            history.push(`/org_location-info/${event.locationId}`);
                                        }
                                    }}
                                    style={{ cursor: event.billId || event.locationId ? 'pointer' : 'default' }}
                                >
                                    <div>
                                        <div className="dashboard-list-title">{event.title}</div>
                                        <div className="dashboard-list-meta">{event.locationName} · {event.organizationName}</div>
                                        <div className="dashboard-list-meta">{event.detail}</div>
                                    </div>
                                    <div className="dashboard-list-right align-left-mobile">
                                        <Badge color={getStatusTone(event.severity)}>{event.severity}</Badge>
                                        <div className="dashboard-list-meta">{formatTimestamp(event.createdAt)}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardBody>
            </Card>
        );
    }

    renderTopLocations() {
        const { history } = this.props;
        const { metrics } = this.state;
        return (
            <Card className="dashboard-panel" data-testid="restaurant-pulse-panel">
                <CardHeader className="dashboard-panel-header">Restaurant pulse</CardHeader>
                <CardBody>
                    {metrics.topLocations.length === 0 ? (
                        <div className="dashboard-empty-state">No location activity found yet.</div>
                    ) : (
                        <Table responsive hover className="dashboard-table" data-testid="restaurant-pulse-table">
                            <thead>
                                <tr>
                                    <th>Location</th>
                                    <th>Organization</th>
                                    <th>Recent revenue</th>
                                    <th>Live open bills</th>
                                    <th>Stale open bills</th>
                                    <th>Payment issues</th>
                                    <th>Guest risk</th>
                                </tr>
                            </thead>
                            <tbody>
                                {metrics.topLocations.map((location) => (
                                    <tr
                                        key={location.id}
                                        onClick={() => history.push(`/org_location-info/${location.id}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>{location.name}</td>
                                        <td>{location.organizationName}</td>
                                        <td>{formatMoney(location.revenue)}</td>
                                        <td>{location.openBills}</td>
                                        <td>{location.staleOpenBills || 0}</td>
                                        <td>{location.attentionPayments}</td>
                                        <td>{location.guestDistressSignals}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>
        );
    }

    render() {
        const { loading, error, metrics } = this.state;

        return (
            <div className="operations-page" data-testid="operations-overview-page">
                {this.renderHero()}
                {loading ? (
                    <div className="dashboard-loading-state">
                        <Spinner color="primary" />
                    </div>
                ) : null}
                {error ? (
                    <Alert color="danger">{error}</Alert>
                ) : null}
                {!loading && !error && metrics ? (
                    <div>
                        {this.renderPilotAlertSummary()}
                        <Row className="bottom-spaced-element">
                            <Col xl="12">
                                <PaymentLedgerExport />
                            </Col>
                        </Row>
                        {this.renderSummaryCards()}
                        {this.renderDrilldownPanel()}
                        <Row>
                            <Col xl="7" className="bottom-spaced-element">
                                {this.renderPaymentAlerts()}
                            </Col>
                            <Col xl="5" className="bottom-spaced-element">
                                {this.renderGuestWatchlist()}
                            </Col>
                        </Row>
                        <Row>
                            <Col xl="7" className="bottom-spaced-element">{this.renderResolvedPaymentHistory()}</Col>
                            <Col xl="5" className="bottom-spaced-element">{this.renderSupportQueue()}</Col>
                        </Row>
                        <Row>
                            <Col xl="12" className="bottom-spaced-element">{this.renderTopLocations()}</Col>
                        </Row>
                    </div>
                ) : null}
            </div>
        );
    }
}

export default withRouter(OperationsOverviewDashboard);
